import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { HttpHandlerService } from './http-handler.service';

export interface IDisplayPairingCode {
  code: string;
  expiresAtUtc: string;
}

export interface IClientDisplayDevice {
  clientDisplayDeviceId: number;
  deviceName: string;
  isActive: boolean;
  createdAtUtc: string;
  lastSeenUtc: string | null;
}

/** Staff-side API for the customer-facing display. The kiosk page itself uses its own
 *  device token and talks to the API directly (see pages/customer-display). */
@Injectable({ providedIn: 'root' })
export class DisplayService {
  constructor(private http: HttpHandlerService) {}

  createPairingCode(): Promise<IDisplayPairingCode> {
    return firstValueFrom(this.http.post<IDisplayPairingCode>('Display/PairingCode', {}));
  }

  listDevices(): Promise<IClientDisplayDevice[]> {
    return firstValueFrom(this.http.get<IClientDisplayDevice[]>('Display/List'));
  }

  deactivate(clientDisplayDeviceId: number): Promise<unknown> {
    return firstValueFrom(this.http.post('Display/Deactivate', { clientDisplayDeviceId }));
  }

  deleteDevice(clientDisplayDeviceId: number): Promise<unknown> {
    return firstValueFrom(this.http.post('Display/Delete', { clientDisplayDeviceId }));
  }

  /** Best-effort push to the tenant's customer displays — never blocks the payment/consent flow. */
  async sendContent(
    type: 'payment-qr' | 'idle' | 'payment-success' | 'consent-request' | 'signature-request',
    amount?: number,
    note?: string,
    requestId?: string,
    customerName?: string,
    mobile?: string,
    address?: string
  ): Promise<void> {
    try {
      await firstValueFrom(this.http.post('Display/Content', { type, amount, note, requestId, customerName, mobile, address }));
    } catch {
      // No display paired / UPI not configured / offline — the staff dialog still works.
    }
  }

  /** True if the tenant has at least one actively paired display. Used to decide whether to
   *  offer "get consent/signature on display" vs. falling back to the in-browser flow. */
  async hasActiveDisplay(): Promise<boolean> {
    try {
      const devices = await this.listDevices();
      return devices.some((d) => d.isActive);
    } catch {
      return false;
    }
  }
}
